import React, { useState } from 'react';
import { Exercise } from '../../../../../models/Exercise';
import { Ticket } from '../../../../../models/Ticket';
import EAModal from '../../../../shared/Modal/EAModal';
import EAButton from '../../../../shared/Buttons/EAButton';
import AddToGoogleCalendar from '../../../../shared/CalendarIntegration/AddToGoogleCalendar';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { State } from '../../../../../store';
import './ReservationModal.scss';
import ReservationModalHeader from "./ReservationModalHeader";
import ReservationDetailsGrid from "./ReservationDetailsGrid";
import useMethodAccount from "../../../../MyAccount/hooks/useMethodAccount";
import HtmlTooltip from "@mui/material/Tooltip";
import EAAlert from "../../../../shared/EAAlert";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise;
  ticket: Ticket;
  onRefreshReservations?: () => void;
}

const ReservationDetailsModal: React.FC<IProps> = ({ isOpen, onClose, exercise, ticket, onRefreshReservations }) => {
  const { t } = useTranslation();
  const [confirmResignation, setConfirmResignation] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resignError, setResignError] = useState("");
  const { resignExercise } = useMethodAccount();

  // Update time when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setNow(Date.now());
    }
  }, [isOpen]);

  // Zeskanowana obecność (Y/U) to fakt niezależny od zegara — bez tego dałoby się pokazać
  // przycisk "Zrezygnuj" dla rezerwacji, na której user już był (check-in przed formalnym
  // startem zajęć).
  const isAttended = exercise.status === "U" || exercise.status === "Y";
  const isEventPassed = isAttended || new Date(exercise.startTime) <= new Date();

  const allowToChangeDate = useSelector((state: State) => state.config.configuration.allowReservedExerciseChange);
  const blockTimeBeforeExercise = useSelector(
    (state: State) => state.config.configuration.blockTimeBeforeExerciseDateChange
  );

  // Check if resign button should be visible and enabled
  const canShowResignButton = (): boolean => {
    if (!allowToChangeDate && !ticket.isExerciseSelfService) {
      return false;
    }
    return true;
  };

  const isResignButtonDisabled = (): { disabled: boolean; tooltip?: string } => {
    const exerciseTime = exercise.startTime < 1e11 ? exercise.startTime * 1000 : exercise.startTime;

    // If event has started
    if (exerciseTime <= now) {
      return {
        disabled: true,
        tooltip:
          t("rezygnacja_niedostepna_rozpoczete_zajecia") ||
          "Rezygnacja jest niedostępna — wydarzenie już się rozpoczęło.",
      };
    }

    // If time to resign has passed
    if (now >= exerciseTime - blockTimeBeforeExercise * 60_000) {
      return {
        disabled: true,
        tooltip:
          t("nie_mozna_zrezygnowac_minal_czas") ||
          "Nie możesz już zrezygnować — minął czas przewidziany na zmianę rezerwacji.",
      };
    }

    return { disabled: false };
  };

  const handleClose = () => {
    setConfirmResignation(false);
    onRefreshReservations?.();
    onClose();
  };

  const handleResignClick = () => {
    setConfirmResignation(true);
  };

  const handleCancelResignation = () => {
    setConfirmResignation(false);
  };

  const handleConfirmResignation = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setConfirmResignation(false);
    setResignError("");
    try {
      const result = await resignExercise(exercise, () => {
        onRefreshReservations?.();
        onClose();
      });
      if (result && !result.ok) {
        setResignError(result.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resignButtonState = isResignButtonDisabled();
  const showResignButton = canShowResignButton() && !isEventPassed;

  // Domyślny tooltip gdy przycisk jest aktywny
  const tooltipMessage = resignButtonState.tooltip || "Możesz zrezygnować z tej rezerwacji";

  return (
    <EAModal isOpen={isOpen} title={t("szczegoly_rezerwacji")} onClose={handleClose} hideButtons desktopWidth="600px">
      <div className="reservation-modal">
        {confirmResignation ? (
          // Confirmation view
          <>
            <h5 style={{ marginBottom: "8px" }}>{t("czy_na_pewno_chcesz_zrezygnowac")}</h5>
            <p className="body-text-small text-color-error" style={{ margin: "0 0 16px" }}>
              {t("rezygnacja_info_brak_zwrotu")}
            </p>
            {resignError && <EAAlert type="error" message={resignError} />}
            <div className="reservation-modal__actions">
              <EAButton
                title={t("tak_rezygnuje")}
                onClick={handleConfirmResignation}
                color="error"
                fullWidth
                loading={isSubmitting}
                loadingText={t("rezygnowanie") || "Rezygnowanie..."}
              />
              <EAButton
                title={t("nie")}
                onClick={handleCancelResignation}
                variant="outlined"
                color="error"
                fullWidth
                disabled={isSubmitting}
              />
            </div>
          </>
        ) : (
          // Details view
          <>
            {/* SEKCJA 1: Header - Data (EAInfoTile), Nazwa, Czas pozostały, Zdjęcie */}
            <ReservationModalHeader exercise={exercise} ticket={ticket} />

            {/* SEKCJA 2: Szczegóły rezerwacji */}
            <ReservationDetailsGrid exercise={exercise} ticket={ticket} />

            {resignError && <EAAlert type="error" message={resignError} />}

            {/* SEKCJA 3: Przyciski */}
            <div className="reservation-modal__actions">
              {showResignButton && (
                <HtmlTooltip
                  placement="top"
                  arrow
                  title={<article dangerouslySetInnerHTML={{ __html: tooltipMessage }} />}
                >
                  <span style={{ flex: 1 }}>
                    <EAButton
                      title={t("zrezygnuj")}
                      onClick={handleResignClick}
                      color="error"
                      disabled={resignButtonState.disabled}
                      fullWidth
                    />
                  </span>
                </HtmlTooltip>
              )}
              <AddToGoogleCalendar exercise={exercise} variant="filled" color="primary" fullWidth noMargins={false} />
            </div>
          </>
        )}
      </div>
    </EAModal>
  );
};

export default ReservationDetailsModal;
